import MarkdownIt from 'markdown-it';
import dayjs from 'dayjs';
import Lodash from 'lodash';
import { callAPI, publishLocal } from '@/store/socket';
import { enqueueUpload } from '@/utils/uploadQueue';
import router from '@/router';
import store from '@/store';

import {
  calculateDominantBrightness,
  generateHash,
  beautySlug,
  attachmentUrl,
  imageToThumbHash,
  openLastProjectOfCurrentWorkspace,
} from '@/store/utils';
import {
  EXISTING__GROUPS,
  EVENT__MOVE,
  EVENT__AUTOCLICK,
  EVENT__UNASSIGN,
  EVENT__ASSIGN,
  EVENT__ASSIGN_IF_NOTASSIGNED,
  EVENT__ASSIGN_MENTION,
} from '@/utils/constantHolder';
import i18n from '@/i18n';


const MAX_USER_AVATARS_COUNT = 8;
const MAX_USER_AVATARS_COUNT_MB = 4;
let lastFilterRequestId = 0;

const pinCurrentUser = (members) => {
  const idx = members.findIndex(pm => pm.userId === store.state.user.user.id);
  if (idx > 0) {
    members.unshift(members.splice(idx, 1)[0]);
  }
};

const md = new MarkdownIt({
  breaks: true,
  linkify: true,
});

function sortingStrategyFunction(sortingStrategy) {
  return sortingStrategy === 'priorityFirst'
    ? (t1, t2) => (t2.priority - t1.priority) + (t2.order - t1.order) / 1.0e5
    : (t1, t2) => t2.order - t1.order;
}

const defaultRender = md.renderer.rules.link_open
  || ((tokens, idx, options, env, self) => self.renderToken(tokens, idx, options));
const defaultImgRender = md.renderer.rules.image;

md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  const newTokens = [...tokens];
  const aIndex = newTokens[idx].attrIndex('target');
  if (aIndex < 0) {
    newTokens[idx].attrPush(['target', '_blank']);
  } else {
    newTokens[idx].attrs[aIndex][1] = '_blank';
  }
  const hIndex = newTokens[idx].attrIndex('href');
  if (hIndex >= 0 && newTokens[idx].attrs[hIndex][1].match(/#[A-z0-9]{22}/)) {
    const cIndex = newTokens[idx].attrIndex('class');
    if (cIndex < 0) {
      newTokens[idx].attrPush(['class', 'user-tag']);
    }
  }
  const dIndex = newTokens[idx].attrIndex('draggable');
  if (dIndex < 0) {
    newTokens[idx].attrPush(['draggable', false]);
  } else {
    newTokens[idx].attrs[dIndex][1] = false;
  }
  return defaultRender(newTokens, idx, options, env, self);
};

md.renderer.rules.image = (tokens, idx, options, env, self) => {
  const newTokens = [...tokens];
  const dIndex = newTokens[idx].attrIndex('draggable');
  if (dIndex < 0) {
    newTokens[idx].attrPush(['draggable', false]);
  } else {
    newTokens[idx].attrs[dIndex][1] = false;
  }
  return defaultImgRender(newTokens, idx, options, env, self);
};

// partial task update carries a filtered field whose new value is outside the active board filter
function updateLeavesActiveFilter(filter, updTask) {
  return [['assigneeIds', 'assigneeId'], ['executorIds', 'executorId']].some(([filterKey, taskKey]) => (
    filter[filterKey] && taskKey in updTask && !filter[filterKey].includes(updTask[taskKey])
  ));
}

// tagIds semantics mirror backend getTasksForStagePromise: falsy id means "no labels", entries are OR-ed
function labelsMatchTagFilter(tagIds, labels) {
  if (!tagIds) {
    return true;
  }
  return tagIds.some(tagId => (tagId ? labels.some(l => l.id === tagId) : labels.length === 0));
}

// tasksCount is intentionally kept: the server's filtered count may still include the task
// (e.g. it re-matches later), and Stage lazy-load bails once tasks.length >= tasksCount;
// the count self-heals on the next filtered fetch
function evictTaskFromStage(stage, taskIndex) {
  stage.tasks.splice(taskIndex, 1);
}

// a transferred task gets its own copy of each attachment file
async function copyAttachmentToTask(source, attachment) {
  const file = await (await fetch(attachmentUrl(source))).blob();
  enqueueUpload({
    file,
    name: source.filename ? `${source.filename}.${source.extension}` : `image.${source.extension}`,
    taskId: attachment.taskId,
    attachment,
  });
}

function findTaskAndStageInProject(state, taskId) {
  let stage = null;
  let task = null;
  let taskIndex = null;
  state.project.stages.some((st) => {
    if (st.tasks) {
      const exTaskIndex = +st.tasks.findIndex(t => t.id === taskId);
      taskIndex = exTaskIndex;
      if (exTaskIndex !== -1) {
        task = st.tasks[exTaskIndex];
        stage = st;
        return true;
      }
    }
    return false;
  });
  return { stage, task, taskIndex };
}

function reduceChildTasksDescriptor(prev = [], update) {
  if (Array.isArray(update)) {
    return update;
  }

  if (!update || typeof update !== "object") {
    return prev;
  }

  const idx = prev.findIndex(t => t.id === update.id);

  if (update.deleted) {
    const next = prev.filter(t => t.id !== update.id);
    return next.length ? next : undefined;
  }

  if (idx === -1) {
    return [...prev, update];
  }

  return prev.map(t =>
    t.id === update.id ? { ...t, ...update } : t
  );
}

const skeletonProjectData = {
  stages: [
    {
      id: '1',
      // loader-only stage: 0 makes Stage.loadMoreTasks bail instead of lazy-loading by a missing count
      tasksCount: 0,
      tasks: [
        {
          id: '345', 
          tags: true, 
          rows: ['threequarters'],
          justLoader: true,
        },
        { id: '231', 
          rows: ['fullwidth', 'fullwidth', 'halfwidth'],
          justLoader: true,
        },
        {
          id: '012', 
          image: { h: '150' }, 
          tags: true, 
          rows: ['fullwidth', 'halfwidth'],
          justLoader: true,
        },
        {
          id: '441', 
          tags: true, 
          rows: ['fullwidth', 'fullwidth', 'halfwidth'],
          justLoader: true,
        },
      ],
    },
    {
      id: '2',
      tasksCount: 0,
      tasks: [
        {
          id: '411', 
          image: { h: '130' }, 
          rows: ['fullwidth'],
          justLoader: true,
        },
        {
          id: '123', image: { h: '98' }, tags: true, 
          rows: ['threequarters'],
          justLoader: true,
        },
        { 
          id: '789', 
          rows: ['fullwidth', 'halfwidth'],
          justLoader: true,
         },
      ],
    },
    {
      id: '3',
      tasksCount: 0,
      tasks: [
        {
          id: '111', 
          image: { h: '170' }, 
          tags: true, 
          rows: ['fullwidth', 'fullwidth', 'halfwidth'],
          justLoader: true,
        },
        {
          id: '222', image: { h: '82' }, 
          rows: ['fullwidth', 'halfwidth'],
          justLoader: true,
        },
      ],
    },
  ],
};

function alertManualTimeRefused(commit, textKey) {
  commit('mutationPushActionAlert', {
    actionText: i18n.global.t(textKey),
    confirmButton: {
      text: 'OK',
      variant: 'red',
      method: () => { commit('mutationShiftActionAlert'); },
    },
  });
}

export default {
  namespaced: true,
  state: {
    templates: [],
    projectsList: [],
    projectsListWithMembers: [],
    project: skeletonProjectData,
    preloadProject: {},
    filterLoading: false,
    projectTasksFilter: {},
    // now, we have two strategies of sorting: priorityFirst - (Kirill asked) and default
    sortingStrategy: '',

    // task popup
    taskUnderEdit: null,
    openedFromParentId: null,
    subtasks: null,

    enableScreenshots: true,
    needToReloadProject: false,
    projectsArchivedList: [],
    projectsArchivedListLoaded: false,
    projectsArchivedListLoading: false,

    actionAlertsQueque: [],
    // Integrations mapping for current project: { [classId]: { botName, logo } } or null while loading
    integrations: null,
    // Subtasks store: { [parentTaskId]: null | Array<Task> }
  },
  mutations: {
    mutationPushActionAlert(state, { actionText, grayButton, confirmButton }) {
      state.actionAlertsQueque = [...state.actionAlertsQueque, { actionText, grayButton, confirmButton }];
    },
    mutationShiftActionAlert(state) {
      const newQueque = [...state.actionAlertsQueque];
      newQueque.shift();
      state.actionAlertsQueque = newQueque;
    },
    mutationCloseTaskPopup(state) {
      state.taskUnderEdit = null;
      state.subtasks = null; // release ram faster
    },
    mutationToReloadProjectState(state, flag) {
      state.needToReloadProject = flag;
    },
    mutationOpenTaskPopup(state, { task }) {
      if (state.taskUnderEdit && task.id === state.taskUnderEdit.id) {
        return;
      }
      console.log('✅✅✅ mutationOpenTaskPopup', task);
      state.openedFromParentId = state.taskUnderEdit && task.parentTaskId === state.taskUnderEdit.id
        ? state.taskUnderEdit.id
        : null;
      state.taskUnderEdit = task;
      const slugNameTask = beautySlug({
        text: task.textMD || 'image',
        lower: true,
        length: 30,
        sliceLength: 15,
      }, 'untitled-task'); 

      router.push({
        name: 'projectTask',
        params: {
          ...(task.project && task.project.name && task.project.slug
            ? {
              projectSlug: task.project.slug,
              slugNameProject: task.project.nameSlug,
            }
            : {}),
          hid: task.hid,
          taskSlug: task.slug,
          slugNameTask,
        },
      });
    },
    mutationEnableScreenshots(state, value) {
      state.enableScreenshots = value;
    },
    markAsRead(state, data) {
      if (state.taskUnderEdit) {
        if (data.option === 'all') {
          const events = state.taskUnderEdit.events.map((el) => {
            if (el.taskComment) {
              const toReadIndex = el.taskComment.commentReadByUser
                .findIndex(com => com.ttbuId === data.ttbuId);
              if (toReadIndex !== -1) {
                el.taskComment.commentReadByUser.splice(toReadIndex, 1);
              }
            }
            return el;
          });
          state.taskUnderEdit.taskEvents = events;
        } else {
          const event = state.taskUnderEdit.taskEvents.find(el => el.taskCommentId
            === data.commentId);
          if (!event) {
            return;
          }
          const commentReadByUsers = event.taskComment.commentReadByUsers
            .findIndex(el => el.ttbuId === data.ttbuId);
          if (commentReadByUsers !== -1) {
            event.taskComment.commentReadByUsers.splice(commentReadByUsers, 1);
          }
        }
      }
    },
    updateTimeSlot(state, timeSlot) {
      store.commit('task/mutationUpdateTimeSlot', timeSlot);

      if (!timeSlot.taskId) {
        console.error('taskId is not defined in timeSlot');
        return;
      }
      const { task } = findTaskAndStageInProject(state, timeSlot.taskId);
      if (!task) {
        console.error('task not found in project on timeSlotUpdate');
        return;
      }
      const ttbuIndex = task.taskTrackedByUsers ? task.taskTrackedByUsers.findIndex(
        t => t.id === timeSlot.taskTrackedByUserId,
      ) : -1;
      if (ttbuIndex === -1) {
        console.error('TTBU not found on timeSlotUpdate');
        return;
      }
      const ttbu = task.taskTrackedByUsers[ttbuIndex];

      if (!ttbu.timeSlot || !ttbu.timeSlot.length) {
        ttbu.timeSlots = [timeSlot];
      } else if (timeSlot.trackStarted > ttbu.timeSlots[0].trackStarted) {
        ttbu.timeSlots[0] = { ...ttbu.timeSlots[0], ...timeSlot };
      } else {
        console.error('Received older Timeslot');
      }
      if (state.taskUnderEdit && ttbu.taskId === state.taskUnderEdit.id) {
        const updatedTask = { ...state.taskUnderEdit };
        if (!updatedTask.taskTrackedByUsers) {
          updatedTask.taskTrackedByUsers = [];
        }
        const ttbuIdx = +updatedTask.taskTrackedByUsers.findIndex(t => t.id === ttbu.id);
        if (ttbuIdx !== -1) {
          const openedTtbu = updatedTask.taskTrackedByUsers[ttbuIdx];
          if (!openedTtbu.timeSlot || !openedTtbu.timeSlot.length) {
            openedTtbu.timeSlots = [timeSlot];
          } else if (timeSlot.trackStarted > openedTtbu.timeSlots[0].trackStarted) {
            openedTtbu.timeSlots[0] = { ...openedTtbu.timeSlots[0], ...timeSlot };
          } else {
            console.error('Received older Timeslot');
          }
          updatedTask.taskTrackedByUsers[ttbuIdx] = openedTtbu;
        }
        state.taskUnderEdit = updatedTask;
      }
    },
    excludeProjectMember(state, data) {
      const idx = state.project.projectMembers.findIndex(pm => pm.id === data.projectMemberId);
      if (idx !== -1) {
        state.project.projectMembers.splice(idx, 1);
      }
    },
    changeProjectMemberTeamRole(state, data) {
      const idx = state.project.projectMembers.findIndex(pm => pm.id === data.projectMemberId);
      if (idx !== -1) {
        state.project.projectMembers[idx].teamRole = data.newTeamRole;
      }
    },
    deleteProject(state, data) {
      const idx = state.projectsList.findIndex(p => p.id === data.projectId);
      if (idx !== -1) {
        state.projectsList.splice(idx, 1);
      }

      if (state.project && state.project.id === data.projectId) {
        if (state.projectsList.length) {
          router.push({
            name: 'project',
            params: {
              projectSlug: state.projectsList[0].slug,
              slugNameProject: state.projectsList[0].nameSlug,
            },
          });
          return;
        }

        router.push({
          path: '/project/No-projects',
        });
      }
    },
    updateTTBU(state, data) {
      function updateTrackedByUsers(task, ttbu) {
        const updatedTask = { ...task };

        if (!updatedTask.taskTrackedByUsers) {
          updatedTask.taskTrackedByUsers = [];
        }

        const ttbuIdx = updatedTask.taskTrackedByUsers.findIndex(t => t.id === ttbu.id);

        if (ttbuIdx !== -1) {
          updatedTask.taskTrackedByUsers[ttbuIdx] = {
            ...updatedTask.taskTrackedByUsers[ttbuIdx],
            ...ttbu,
          };
        } else {
          updatedTask.taskTrackedByUsers.push(ttbu);
        }

        return updatedTask;
      }
      if (state.taskUnderEdit && data.ttbu.taskId === state.taskUnderEdit.id) {
        state.taskUnderEdit = updateTrackedByUsers(state.taskUnderEdit, data.ttbu);
      }

      const activeTask = store.state.task?.activeTrackingTask;
      if (activeTask && data.ttbu.taskId === activeTask.id) {
        const updatedTask = updateTrackedByUsers(activeTask, data.ttbu);
        store.commit('task/mutationUpdateDataInTrackingTask', updatedTask);
      }
      const project = data.projectId ? state.projectsList.find(p => p.id === data.projectId) : state.project;
      const updated = project.stages.some((stage) => {
        const exTaskIndex = +stage.tasks.findIndex(t => t.id === data.ttbu.taskId);
        if (exTaskIndex !== -1) {
          const updatedTask = { ...stage.tasks[exTaskIndex] };
          if (!updatedTask.taskTrackedByUsers) {
            updatedTask.taskTrackedByUsers = [];
          }
          const ttbuIdx = +updatedTask.taskTrackedByUsers.findIndex(t => t.id === data.ttbu.id);
          if (ttbuIdx !== -1) {
            updatedTask.taskTrackedByUsers[ttbuIdx] = {
              ...updatedTask.taskTrackedByUsers[ttbuIdx],
              ...data.ttbu,
            };
          } else {
            updatedTask.taskTrackedByUsers.push(data.ttbu);
          }
          stage.tasks[exTaskIndex] = { ...stage.tasks[exTaskIndex], ...updatedTask };

          if (data.ttbu?.trackingOnline !== undefined && data.ttbu?.userId) {
            if (data.ttbu.trackingOnline === true) {
              project.activeTrackUserIds = [...project.activeTrackUserIds,
                data.ttbu.userId];
            } else if (data.ttbu.trackingOnline === false) {
              project.activeTrackUserIds = project.activeTrackUserIds
                .filter(id => id !== data.ttbu.userId);
            }
          }
          return true;
        }
        return false;
      });

      if (!updated) {
        console.error('Can\'t update ttbu for current task');
      }
    },
    setElements(state, data) {
      if (!state.taskUnderEdit) {
        console.error('Can\'t add elements to current task, taskUnderEdit is not set');
        return
        // state.taskUnderEdit = {
        //   attachments: [],
        // };
      }
      const updatedTask = state.taskUnderEdit;
      updatedTask.taskEvents = updatedTask.taskEvents ? [...updatedTask.taskEvents] : [];
      data.elements.forEach((el) => {
        if (data.category === 'attachment') {
          if (!updatedTask.attachments) {
            updatedTask.attachments = [];
          }
          const elIdx = +updatedTask.attachments.findIndex(a => a.id === el.id);
          if (elIdx === -1) {
            updatedTask.attachments.push(el);
          }
        } else if (data.category === 'screenshot') {
          if (!updatedTask.screenshots) {
            updatedTask.screenshots = [];
          }
          const elIdx = +updatedTask.screenshots.findIndex(s => s.id === el.id);
          if (elIdx === -1) {
            updatedTask.screenshots.push(el);
          }
        } else if (data.category === 'taskEvent') {
          const elIdx = +updatedTask.taskEvents.findIndex(e => e.id === el.id);
          if (elIdx === -1) {
            updatedTask.taskEvents.push(el);
          }
        } else if (data.category === 'taskComment') {
          if (!updatedTask.comments) {
            updatedTask.comments = [];
          }
          const elIdx = +updatedTask.comments.findIndex(e => e.id === el.id);
          if (elIdx === -1) {
            updatedTask.comments.push(el);
          }
        } else {
          throw new Error('Can\'t add element to current task, no such category');
        }
      });
    },
    dragTask(state, data) {
      const { task, stageIdFrom } = data;

      if (task.projectId !== state.project.id) {
        console.warn('dragTask error: Got task update for nonactive project'); 
        return;
      }
      // remove task from prev stage
      const stageFrom = state.project.stages.find(s => s.id === stageIdFrom);
      const oldTask = stageFrom.tasks.find(s => s.id === task.id);
      const newTasks = stageFrom.tasks.filter(t => t.id !== task.id);
      stageFrom.tasks = [...newTasks];
      if (oldTask) stageFrom.tasksCount -= 1;
      // add task to next stage and change order of higher tasks
      const stageTo = state.project.stages.find(s => s.id === task.stageId);
      const containTask = stageTo.tasks.find(t => t.id === task.id);
      if (!containTask) {
        const movedTask = { ...oldTask, ...task };
        if (task.order === undefined) {
          movedTask.order = Math.max(0, ...stageTo.tasks.map(t => t.order)) + 1;
        }
        const newTasksOfNewStage = [
          movedTask,
          ...stageTo.tasks,
        ];
        newTasksOfNewStage.sort(sortingStrategyFunction(state.sortingStrategy));
        stageTo.tasks = newTasksOfNewStage;
        stageTo.tasksCount += 1;
      } else {
        stageTo.tasks = stageTo.tasks
          .map(t => (t.id === task.id ? { ...t, ...task } : t))
          .sort(sortingStrategyFunction(state.sortingStrategy));
      }
      if (state.taskUnderEdit && state.taskUnderEdit.id === task.id) {
        state.taskUnderEdit = { ...state.taskUnderEdit, ...task };
      }
    },
    setTemplates(state, data) {
      state.templates = data;
    },
    setProjectsList(state, data) {
      state.projectsList = data.map((project) => ({ ...project }));
    },
    setProjectsArchivedList(state, data) {
      state.projectsArchivedList = data.map((project) => ({ ...project }));
      state.projectsArchivedListLoaded = true;
    },
    setProjectsArchivedListLoading(state, loading) {
      state.projectsArchivedListLoading = loading;
    },
    setProjectsListWithMembers(state, data) {
      state.projectsListWithMembers = data;
    },
    setPreloadProject(state, data) {
      console.log('🚩 setPreloadProject', data);
      // we need clone project here to avoid Vue reactivity issues(started to see in vue3)
      state.preloadProject = { ...data };

    },
    updateProjectInList(state, data) {
      if (!data.id) {
        console.error('No id of updated project', data);
        return;
      }
      const newData = { ...data };
      if (newData.rules && typeof newData.rules === 'string') {
        newData.rules = JSON.parse(newData.rules);
      }

      // no source list means some new project appeared
      const sourceList = [state.projectsList, state.projectsArchivedList]
        .find(list => list.some(p => p.id === newData.id));
      const project = { ...sourceList?.find(p => p.id === newData.id), ...newData };
      const targetList = project.archived ? state.projectsArchivedList : state.projectsList;
      if (sourceList === targetList) {
        targetList[targetList.findIndex(p => p.id === project.id)] = project;
        return;
      }
      if (sourceList) {
        sourceList.splice(sourceList.findIndex(p => p.id === project.id), 1);
      }
      targetList.push(project);
    },
    updateProject(state, data) {
      console.log('🚩 updateProject', data);
      const newData = { ...data };
      if (newData.rules) {
        newData.rules = JSON.parse(newData.rules);
      }
      state.project = { ...state.project, ...newData };
      state.preloadProject = { ...state.preloadProject, ...newData };
    },
    setCurrentProject(state, data) {
      const newDate = { ...data };
      if (newDate.rules && typeof newDate.rules === 'string') {
        newDate.rules = JSON.parse(newDate.rules);
      }

      if (newDate.stages) {
        newDate.stages.sort((a, b) => a.order - b.order);
      }

      state.enableScreenshots = data.enableScreenshots;
      state.project = newDate;
    },
    setProjectIntegrations(state, data) {
      state.integrations = data;
    },
    setOpenedTask(state, task) {
      // taskEvents might be already loaded, so just merge to get more details
      console.log('🚩🚩 🚩 setOpenedTask', task);
      const t = { ...task };
      state.taskUnderEdit = { ...state.taskUnderEdit, ...t };
    },
    addScreenshot(state, data) {
      if (state.taskUnderEdit && data.screenshot.taskId === state.taskUnderEdit.id) {
        if (!state.taskUnderEdit.screenshots) {
          state.taskUnderEdit.screenshots = [];
        }
        if (!state.taskUnderEdit.screenshots.find(ss => ss.id === data.screenshot.id)) {
          state.taskUnderEdit.screenshots.unshift(data.screenshot);
        }
      }
    },
    addTaskEvent(state, data) {
      if (state.taskUnderEdit && data.event.taskId === state.taskUnderEdit.id) {
        const updatedTask = { ...state.taskUnderEdit };
        if (data.event.groupEventId) {
          const eventIdx = +updatedTask.taskEvents.findIndex(e => e.id === data.event.groupEventId);
          if (eventIdx !== -1) {
            const newGroupEventText = updatedTask.taskEvents[eventIdx].groupEventText
              ? JSON.parse(updatedTask.taskEvents[eventIdx].groupEventText)
              : [];
            newGroupEventText.push(data.event);
            updatedTask.taskEvents[eventIdx].groupEventText = JSON.stringify(newGroupEventText);

            state.taskUnderEdit = updatedTask;
          }
        } else if (data.event.groupEventId === null) {
          const eventIdx = +updatedTask.taskEvents.findIndex(e => e.id === data.event.id);
          if (eventIdx !== -1) {
            updatedTask.taskEvents.splice(eventIdx, 1);
          }
          updatedTask.taskEvents.push(data.event);
          state.taskUnderEdit = updatedTask;
        } else {
          console.log('else', JSON.stringify(state.taskUnderEdit, null, 2));
        }
      }
    },
    deleteScreenshot(state, screenshot) {
      if (state.taskUnderEdit && screenshot.taskId === state.taskUnderEdit.id) {
        const updatedTask = { ...state.taskUnderEdit };
        const deleteIndex = updatedTask.screenshots?.findIndex(ss => ss.id === screenshot.id);
        if (deleteIndex !== undefined && deleteIndex !== -1) {
          updatedTask.screenshots.splice(deleteIndex, 1);
        } else {
          console.log('Screenshot has been deleted from server');
        }
        state.taskUnderEdit = updatedTask;
      }
      store.commit('user/mutationRemoveScreenshot', screenshot);
    },
    addAttachment(state, data) {
      if (data.attachment.type === 'TASK_PREVIEW') {
        const stage = state.project.stages.find(s => s.id === data.stageId);
        const exTaskIndex = +stage.tasks.findIndex(t => t.id === data.taskId);
        if (exTaskIndex !== -1) {
          const updTask = { ...stage.tasks[exTaskIndex] };
          const attachIndex = updTask.attachments.findIndex(a => a.id === data.attachment.id);
          if (attachIndex === -1) {
            updTask.attachments = [data.attachment, ...updTask.attachments];
          } else {
            const newAttachment = data.attachment;
            if (!newAttachment.basePicture && updTask.attachments[attachIndex].basePicture) {
              newAttachment.basePicture = updTask.attachments[attachIndex].basePicture;
            }
            updTask.attachments[attachIndex] = newAttachment;
          }
          stage.tasks[exTaskIndex] = { ...stage.tasks[exTaskIndex], ...updTask };
          console.log('stage.tasks[exTaskIndex] 📈↗️↖️',
            JSON.parse(JSON.stringify(stage.tasks[exTaskIndex])));
        } else {
          console.log('⚠️⚠️⚠️ Task is not accessable from the stage!');
        }
      }
      if (state.taskUnderEdit && data.taskId === state.taskUnderEdit.id) {
        const updTask = { ...state.taskUnderEdit };
        const attachIndex = updTask.attachments.findIndex(a => a.id === data.attachment.id);
        const { attachment } = data;
        if (attachIndex === -1) {
          updTask.attachments = [{ ...attachment, send: true }, ...updTask.attachments];
        } else {
          // eslint-disable-next-line
          if (!attachment.basePicture && updTask.attachments[attachIndex].basePicture) {
            attachment.basePicture = updTask.attachments[attachIndex].basePicture;
          }
          if (updTask.attachments[attachIndex].send) {
            attachment.send = true;
          }
          updTask.attachments[attachIndex] = attachment;
        }
        state.taskUnderEdit = { ...state.taskUnderEdit, ...updTask };
      }
    },
    deleteAttachment(state, attachment) {
      if (attachment.type === 'TASK_PREVIEW') {
        const { stage, taskIndex: exTaskIndex } = findTaskAndStageInProject(
          state, attachment.taskId,
        );
        if (exTaskIndex !== -1) {
          const updatedTask = { ...stage.tasks[exTaskIndex] };
          const deleteIndex = updatedTask.attachments.findIndex(a => a.id === attachment.id);
          if (deleteIndex !== -1) {
            updatedTask.attachments.splice(deleteIndex, 1);
          } else {
            console.log('Attachment has been deleted from server');
          }
          stage.tasks[exTaskIndex] = updatedTask;
        } else {
          throw new Error('Can\'t delete attachment from current task');
        }
      }
      if (state.taskUnderEdit && attachment.taskId === state.taskUnderEdit.id) {
        const updatedTask = { ...state.taskUnderEdit };
        const deleteIndex = updatedTask.attachments.findIndex(a => a.id === attachment.id);
        if (deleteIndex !== -1) {
          updatedTask.attachments.splice(deleteIndex, 1);
        } else {
          console.log('Attachment has been deleted from server');
        }
        state.taskUnderEdit = { ...state.taskUnderEdit, ...updatedTask };
      }
    },
    updateAttachments(state, data) {
      // TODO: why not update one (updateAttachment[remove s])
      const stage = state.project.stages.find(s => s.id === data.stageId);
      const exTaskIndex = +stage.tasks.findIndex(t => t.id === data.taskId);

      const currentTaskId = state.taskUnderEdit ? state.taskUnderEdit.id : null;
      if (exTaskIndex !== -1) {
        const updatedTask = { ...stage.tasks[exTaskIndex] };
        data.attachments.forEach((attachment) => {
          const attachmentIndex = updatedTask.attachments.findIndex(a => a.id === attachment.id);
          if (attachmentIndex !== -1) {
            if (attachment.deleted) {
              updatedTask.attachments.splice(attachmentIndex, 1);
            } else {
              updatedTask.attachments[attachmentIndex] = {
                ...updatedTask.attachments[attachmentIndex],
                ...attachment,
              };
            }
          } else if (!attachment.deleted) {
            updatedTask.attachments.push(attachment);
          }
        });
        stage.tasks[exTaskIndex] = updatedTask;
      } else if (data.taskId === currentTaskId) {
        const updatedTask = { ...state.taskUnderEdit };
        data.attachments.forEach((attachment) => {
          const attachmentIndex = updatedTask.attachments.findIndex(a => a.id === attachment.id);
          if (attachmentIndex !== -1) {
            if (attachment.deleted) {
              updatedTask.attachments.splice(attachmentIndex, 1);
            } else {
              updatedTask.attachments[attachmentIndex] = {
                ...updatedTask.attachments[attachmentIndex],
                ...attachment,
              };
            }
          } else if (!attachment.deleted) {
            updatedTask.attachments.push(attachment);
          }
        });
        state.taskUnderEdit = { ...updatedTask };
      } else {
        throw new Error('Can\'t update attachments for the current task: task is not accessable');
      }
      if (state.taskUnderEdit && data.taskId === state.taskUnderEdit.id) {
        const updatedTask = { ...state.taskUnderEdit };
        data.attachments.forEach((attachment) => {
          const attachmentIndex = updatedTask.attachments.findIndex(a => a.id === attachment.id);
          if (attachmentIndex !== -1) {
            if (attachment.deleted) {
              updatedTask.attachments.splice(attachmentIndex, 1);
            } else {
              updatedTask.attachments[attachmentIndex] = {
                ...updatedTask.attachments[attachmentIndex],
                ...attachment,
              };
            }
          } else if (!attachment.deleted) {
            updatedTask.attachments.push(attachment);
          }
        });
        state.taskUnderEdit = { ...state.taskUnderEdit, ...updatedTask };
      }
    },
    updateAttachment(state, data) {
      const { task } = findTaskAndStageInProject(state, data.taskId);
      if (task) {
        const attachIdx = task.attachments.findIndex(a => a.id === data.id);
        if (attachIdx !== -1) {
          task.attachments[attachIdx] = { ...task.attachments[attachIdx], ...data };
        }
      }
      if (state.taskUnderEdit && state.taskUnderEdit.id === data.taskId) {
        const attachIdx = state.taskUnderEdit.attachments.findIndex(a => a.id === data.id);
        const updatingAttchmentData = data;

        if (attachIdx !== -1) {
          state.taskUnderEdit.attachments[attachIdx] = { 
            ...state.taskUnderEdit.attachments[attachIdx], 
            ...updatingAttchmentData };
        }
      }
    },
    setLabelForTask(state, {
      task, label, add, clear,
    }) {
      if (task.projectId === state.project.id) {
        // received publish to project
        if (!state.project.labels.some(l => l.id === label.id)) {
          state.project.labels.push(label);
        }
        const stage = state.project.stages.find(s => s.id === task.stageId);
        const exTaskIndex = +stage.tasks.findIndex(t => t.id === task.id);
        if (exTaskIndex !== -1) {
          const updTask = { ...task };
          if (add) {
            if (!updTask.labels) {
              updTask.labels = [];
            }
            if (!updTask.labels.some(l => l.id === label.id)) {
              updTask.labels.push(label);
            }
          }
          if (clear) {
            const labelIndex = updTask.labels.findIndex(l => l.id === label.id);
            if (labelIndex !== -1) {
              updTask.labels.splice(labelIndex, 1);
            }
          }
          const merged = { ...stage.tasks[exTaskIndex], ...updTask };
          if (labelsMatchTagFilter(state.projectTasksFilter.tagIds, merged.labels)) {
            stage.tasks[exTaskIndex] = merged;
          } else {
            // task no longer matches the active tag filter
            evictTaskFromStage(stage, exTaskIndex);
          }
        }

        // also in this case might be one of subtasks

        if (state.subtasks !== null) {
          console.log('checking subtasks for label update', {
            subtasks: state.subtasks, 
            jdumps: JSON.stringify(state.subtasks)
          });
          const subtaskIndex = state.subtasks.findIndex(st => st.id === task.id);
          if (subtaskIndex !== -1) {
            const subtask = state.subtasks[subtaskIndex];
            subtask.labels = subtask.labels || [];
            if (add) {
              if (!subtask.labels.some(l => l.id === label.id)) {
                subtask.labels.push(label);
              }
            }
            if (clear) {
              const labelIndex = subtask.labels.findIndex(l => l.id === label.id);
              if (labelIndex !== -1) {
                subtask.labels.splice(labelIndex, 1);
              }
            }
          }
        }
      }
      // also this publish might be received to myTasks
      if (router.currentRoute.value.name === 'myTasks') {
        const groups = store.state.user && store.state.user.myTasksByGroup;

        console.log('checking myTasks groups for label update', {
          groups: groups.map(g => ({
            id: g.id,
            tasks: g.tasks ? g.tasks.map(t => t.id) : [],
          })),
          jdumps: JSON.stringify(groups),
        });

        if (groups && groups.length) {
          // find task across groups and update its labels
          groups.some((group) => {
            const tIdx = group.tasks ? group.tasks.findIndex(t => t.id === task.id) : -1;
            if (tIdx !== -1) {
              const upd = { ...group.tasks[tIdx] };
              upd.labels = upd.labels || [];
              if (add) {
                if (!upd.labels.some(l => l.id === label.id)) {
                  upd.labels.push(label);
                }
              }
              if (clear) {
                const lIdx = upd.labels.findIndex(l => l.id === label.id);
                if (lIdx !== -1) {
                  upd.labels.splice(lIdx, 1);
                }
              }
              group.tasks[tIdx] = { ...group.tasks[tIdx], ...upd };
              return true; // stop searching further groups
            }
            return false;
          });
        }
      }

      // also it might arrive to taskUnderEdit
      if (router.currentRoute.value.name === 'projectTask' &&
          state.taskUnderEdit && state.taskUnderEdit.id === task.id) {
        const updatedTask = { ...state.taskUnderEdit };
        updatedTask.labels = updatedTask.labels || [];

        if (add) {
          if (!updatedTask.labels.some(l => l.id === label.id)) {
            updatedTask.labels.push(label);
          }
        }
        if (clear) {
          const lIdx = updatedTask.labels.findIndex(l => l.id === label.id);
          if (lIdx !== -1) {
            updatedTask.labels.splice(lIdx, 1);
          }
        }
        state.taskUnderEdit = updatedTask;
      }
    },
    removeLabel(state, labelId) {
      console.log('removing label with id', labelId);
      for (let i = 0; i < state.project.stages.length; i += 1) {
        for (let j = 0; j < state.project.stages[i].tasks.length; j += 1) {
          const updTask = { ...state.project.stages[i].tasks[j] };
          const labelIndex = updTask.labels.findIndex(l => l.id === labelId);
          console.log('label index in task', labelIndex);
          if (labelIndex !== -1) {
            updTask.labels.splice(labelIndex, 1);
          }
        }
      }
      const labelIndex = state.project.labels.findIndex(l => l.id === labelId);
      console.log('label index in project', labelIndex);
      if (labelIndex !== -1) {
        state.project.labels.splice(labelIndex, 1);
      }
    },
    setProjectTasksFilter(state, {
      assigneeIds, executorIds,
      tagIds, createdByIds, searchString, createdAtRange,
    }) {
      state.projectTasksFilter = {
        assigneeIds, executorIds, tagIds, createdByIds, searchString, createdAtRange,
      };
      if (executorIds || assigneeIds || tagIds || createdByIds || createdAtRange || searchString) {
        state.sortingStrategy = 'priorityFirst'; // to apply correct sorting
      } else {
        state.sortingStrategy = '';
      }
    },
    updateLabel(state, data) {
      for (let i = 0; i < state.project.stages.length; i += 1) {
        for (let j = 0; j < state.project.stages[i].tasks.length; j += 1) {
          const labelIndex = state.project.stages[i].tasks[j].labels.findIndex(
            l => l.id === data.id,
          );

          if (labelIndex !== -1) {
            state.project.stages[i].tasks[j].labels[labelIndex] =
              { ...state.project.stages[i].tasks[j].labels[labelIndex], ...data };
          }
        }
      }
      const labelIndex = state.project.labels.findIndex(l => l.id === data.id);
      if (labelIndex !== -1) {
        state.project.labels[labelIndex] = { ...state.project.labels[labelIndex], ...data };
      } else if (labelIndex === -1 && data.archived === false) {
        state.project.labels[state.project.labels.length] = data;
      }
    },
    updatePriority(state, { userId, projectId, priority }) {
      const memberIndex = state.projectsListWithMembers.findIndex(
        pm => pm.userId === userId && pm.project.id === projectId,
      );
      if (memberIndex !== -1) {
        const updMember = { ...state.projectsListWithMembers[memberIndex] };
        updMember.priority = priority;
        state.projectsListWithMembers[memberIndex] = { 
          ...state.projectsListWithMembers[memberIndex], ...updMember };
      }
    },
    updateTask(state, task) {


      let needSortStage = false;
      if (task.projectId !== state.project.id) {
        // might be ok case - e.g. whemn myTask open
        console.warn('Got task update for nonactive project, ignoring');
        return;
      }
      const updTask = { ...task };
      delete updTask.screenshots;
      delete updTask.taskEvents;
      delete updTask.taskTrackedByUsers;

      let { stage, taskIndex } = findTaskAndStageInProject(state, task.id);

      console.log('⬆️✔️ updateTask, ', JSON.stringify({updTask, taskIndex}, null, 2));
      
      if (taskIndex !== -1) {
        const currentTask = stage.tasks[taskIndex];
        const childTasksDescriptor = task.childTasksDescriptor;

        if (childTasksDescriptor !== undefined) {
          currentTask.childTasksDescriptor = reduceChildTasksDescriptor(
            currentTask.childTasksDescriptor,
            childTasksDescriptor
          );
        }

        delete updTask.childTasksDescriptor;
        if (task.attachments && task.attachments.length > 0) {
          const at = [...task.attachments];
          at.sort((a, b) => dayjs(b.createdAt) - dayjs(a.createdAt));
          updTask.attachments = at;
        }
        if (updateLeavesActiveFilter(state.projectTasksFilter, updTask)) {
          // task no longer matches the active board filter
          evictTaskFromStage(stage, taskIndex);
        } else {
          stage.tasks[taskIndex] = { ...currentTask, ...updTask };
          if (updTask.order !== undefined || updTask.priority !== undefined) {
            needSortStage = true;
          }
          if (updTask.stageId && updTask.stageId !== stage.id) {
            // task changed stage, remove from current stage
            stage.tasks.splice(taskIndex, 1);
            stage.tasksCount -= 1;
            // insert task to new stage
            const newStage = state.project.stages.find(s => s.id === updTask.stageId);
            if (!newStage) {
              console.error('Can\'t find new stage for task after update', JSON.stringify(updTask));
              return;
            }
            newStage.tasks.unshift({ ...currentTask, ...updTask });
            newStage.tasksCount += 1;
            // resort new stage
            newStage.tasks.sort(sortingStrategyFunction(state.sortingStrategy));

            // no sense to sort old stage, as task is removed
            needSortStage = false;
          }
        }
      } else {
        if (task.textMD !== undefined && task.order !== undefined) {
          // textMD + order in task update means this is a new/restored task; title and description
          // edits of tasks not loaded on the board also carry textMD, but never order
          stage = state.project.stages.find(s => s.id === task.stageId);
          stage.tasks.unshift(task);
          needSortStage = true;
          stage.tasksCount += 1;
        } else {
          console.error('Received task update (not a new task: no textMD/order), but task was not found in project, probably it is not at top of project', JSON.stringify(task));
        }
        
      }
      if (needSortStage) {
        if (stage.tasks[0].order === undefined || stage.tasks[0].order === null) {
          console.error(`We have task at 0 index in stage ${stage.id} without order, this is not considered as possible and might cause future error / sorting issues`);
        } else {
          stage.tasks.sort(sortingStrategyFunction(state.sortingStrategy));
          stage.tasks = [...stage.tasks];
        }
      }

      if (state.taskUnderEdit && task.id === state.taskUnderEdit.id) {
        if (updTask.attachments) {
          updTask.attachments = updTask.attachments.map((attachment) => {
            // eslint-disable-next-line
            const attachmentIndex = state.taskUnderEdit.attachments.findIndex(a => a.id === attachment.id);
            if (attachmentIndex !== -1) {
              return { ...state.taskUnderEdit.attachments[attachmentIndex], ...attachment };
            }
            return attachment;
          });
        }
        state.taskUnderEdit = { ...state.taskUnderEdit, ...updTask };
      }

      store.commit('task/mutationUpdateDataInTrackingTask', updTask);

      if (task.parentTaskId && state.taskUnderEdit && task.parentTaskId === state.taskUnderEdit.id) {
        // received update for one of subtasks of opened task
        if (!state.subtasks) {
          state.subtasks = [];
        }
        const subtaskIndex = state.subtasks.findIndex(
          st => st.id === task.id,
        );

        if (subtaskIndex !== -1) {
          state.subtasks[subtaskIndex] = { ...state.subtasks[subtaskIndex], ...updTask };
        } else if (updTask.textMD !== undefined) {
          state.subtasks.unshift(updTask);
        }
        if (!state.taskUnderEdit.isParent) {
          state.taskUnderEdit.isParent = true;
        }
      }
    },
    deleteTask(state, task) {
      if (task.projectId !== state.project.id) {
        throw new Error('Got task update for nonactive project');
      }
      const stage = state.project.stages.find(s => s.id === task.stageId);
      const exTaskIndex = +stage.tasks.findIndex(t => t.id === task.id);
      if (exTaskIndex === -1) {
        return;
      }
      stage.tasks.splice(exTaskIndex, 1);
      stage.tasksCount -= 1;
    },
    projectMemberUpdate(state, { projectId, projectMember }) {
      if (projectId !== state.project.id) {
        return;
      }

      const projectMembers = [...state.project.projectMembers];
      const exIndex = +projectMembers.findIndex(pm => pm.id === projectMember.id);

      const newProjectMemberData = { ...projectMember };
      if (newProjectMemberData.newId) {
        newProjectMemberData.id = projectMember.newId;
        delete newProjectMemberData.newId;
      }

      if (exIndex !== -1) {
        projectMembers[exIndex] = { ...projectMembers[exIndex], ...newProjectMemberData };
      } else {
        projectMembers.push(newProjectMemberData);
      }
      state.project = { ...state.project, projectMembers };
    },
    projectMemberRemove(state, { projectId, pmId }) {
      if (projectId !== state.project.id) {
        return;
      }
      const exIndex = +state.project.projectMembers.findIndex(pm => pm.id === pmId);
      if (exIndex !== -1) {
        state.project.projectMembers.splice(exIndex, 1);
      }
    },
    deleteStage(state, id) {
      const exIndex = +state.project.stages.findIndex(s => s.id === id);
      if (exIndex !== -1) {
        state.project.stages.splice(exIndex, 1);
      }
      if (state.taskUnderEdit && state.taskUnderEdit.stageId === id) {
        router.replace({
          name: 'project',
          params: {
            projectId: state.project.id,
            taskId: null,
            slug: null,
          },
        });
        state.taskUnderEdit = null;
      }
    },
    updateStage(state, stage) {
      const exStageIndex = +state.project.stages.findIndex(s => s.id === stage.id);
      if (stage.projectId !== state.project.id) {
        throw new Error('Got stage update for nonactive project');
      }
      if (exStageIndex !== -1) {
        if (state.project.stages[exStageIndex].tasks) {
          state.project.stages[exStageIndex].tasks.forEach((task) => {
            const t = task;
            if (t.taskEvents) {
              t.taskEvents.forEach((e) => {
                const event = e;
                if (event.stageId === stage.id) {
                  event.stage = stage;
                }
              });
            }
          });
        }
        state.project.stages[exStageIndex] = { ...state.project.stages[exStageIndex], ...stage };
      } else {
        // add stage
        const newStage = { ...stage };
        if (!newStage.tasks) {
          newStage.tasks = [];
        }
        state.project.stages.push(newStage);
      }
    },
    createComment(state, comment) {
      if (state.taskUnderEdit && state.taskUnderEdit.id === comment.taskId) {
        const updTask = { ...state.taskUnderEdit };
        const events = updTask.taskEvents ? [...updTask.taskEvents] : [];
        const eventNewTask = {
          ...comment.taskEvent,
          user: { ...comment.user },
          taskComment: { ...comment },
        };
        events.push(eventNewTask);
        updTask.taskEvents = events;
        state.taskUnderEdit = { ...updTask };
      }
    },
    updateComment(state, comment) {
      if (state.taskUnderEdit && state.taskUnderEdit.id === comment.taskId) {
        const updTask = state.taskUnderEdit;
        const events = updTask.taskEvents || [];
        const event = events.find(e => e.taskCommentId === comment.id);
        Object.keys(comment).forEach((key) => {
          if (event && event.taskComment) {
            event.taskComment[key] = comment[key];
          }
        });
      }
      if (comment.taskDescription) { // taskDescription does not exist?
        const taskDescription = comment.textMD || (comment.textDelta
          ? JSON.parse(comment.textDelta).ops.reduce((acc, el) => `${acc}${el.insert}`, '').replace(/\n/g, ' ') || state.taskUnderEdit.textMD || '' : state.taskUnderEdit.textMD || '');
        if (taskDescription) {
          if (state.taskUnderEdit && state.taskUnderEdit.id === comment.taskId) {
            state.taskUnderEdit.textMD = taskDescription;
          }
          const taskInStage = state.project.stages.reduce((acc, s) => {
            if (acc) return acc;

            const task = s.tasks.find(t => t.id === comment.taskId);
            if (task) return task;

            return acc;
          }, null);
          if (taskInStage) {
            const stageIndex = state.project.stages.findIndex(s => s.id === taskInStage.stageId);
            if (stageIndex >= 0) {
              const taskIndex = state.project.stages[stageIndex].tasks
                .findIndex(t => t.id === taskInStage.id);
              if (taskIndex >= 0) {
                state.project.stages[stageIndex].tasks[taskIndex].textMD = taskDescription;
              }
            }
          }
        }
      }
    },
    deleteComment(state, { id, taskId }) {
      if (state.taskUnderEdit && state.taskUnderEdit.id === taskId) {
        const updTask = { ...state.taskUnderEdit };
        const events = [...updTask.taskEvents];
        const eventIdx = +events.findIndex(c => c.taskCommentId === id);
        if (eventIdx !== -1) {
          events.splice(eventIdx, 1);
        } else {
          return;
        }
        updTask.taskEvents = events;
        state.taskUnderEdit = { ...updTask };
      }
    },
    // Set reaction counts + who-reacted for a single comment (from optimistic update or live event)
    setCommentReactions(state, { commentId, taskId, userReactions, whoReacted }) {
      if (state.taskUnderEdit && state.taskUnderEdit.id === taskId && state.taskUnderEdit.taskEvents) {
        const event = state.taskUnderEdit.taskEvents.find(e => e.taskCommentId === commentId);
        if (event && event.taskComment) {
          event.taskComment.userReactions = userReactions;
          event.taskComment.whoReacted = whoReacted;
        }
      }
    },
    // Bulk-apply the lazily loaded who-reacted map: { [commentId]: { "👍": [userId, ...] } }
    setWhoReacted(state, { taskId, whoReactedByComment }) {
      if (state.taskUnderEdit && state.taskUnderEdit.id === taskId && state.taskUnderEdit.taskEvents) {
        state.taskUnderEdit.taskEvents.forEach((event) => {
          if (event.taskComment && whoReactedByComment[event.taskComment.id]) {
            event.taskComment.whoReacted = whoReactedByComment[event.taskComment.id];
          }
        });
      }
    },
    updateRules(state, { rules, projectId }) {
      if (state.project && state.project.id === projectId) {
        state.project.rules = rules;
        // backend passed string here instead of objects, better not stringify it
        // Have not touch because there are string replaces on next ETL cycles..)
      }
    },
    updateManualTime(state, data) {
      if (state.taskUnderEdit && state.taskUnderEdit.id === data.taskId) {
        if (state.taskUnderEdit.manualTime && state.taskUnderEdit.manualTime.length) {
          const mtIdx = state.taskUnderEdit.manualTime.findIndex(mt => mt.id === data.id);
          if (mtIdx !== -1) {
            state.taskUnderEdit.manualTime[mtIdx] = { ...state.taskUnderEdit.manualTime[mtIdx], ...data };
          } else {
            state.taskUnderEdit.manualTime.push(data);
          }
        } else {
          state.taskUnderEdit.manualTime = [data];
        }
      }
    },
    addTasksToStage(state, data) {
      const stageIndex = +state.project.stages.findIndex(s => s.id === data.stageId);
      if (stageIndex !== -1) {
        if (state.project.stages[stageIndex].tasks.length) {
          console.log('Adding tasks to stage', JSON.stringify(state.project.stages[stageIndex].tasks, null, 2));

          const newTasks = state.project.stages[stageIndex].tasks.concat(data.tasks);
          if (newTasks[0].order !== undefined) {
            newTasks.sort(sortingStrategyFunction(state.sortingStrategy));
          }
          state.project.stages[stageIndex].tasks = newTasks.filter(t => !t.justLoader);
        } else {
          state.project.stages[stageIndex].tasks = data.tasks;
        }
      }
    },
    updateStageTasksCount(state, data) {
      const stageIndex = +state.project.stages.findIndex(s => s.id === data.stageId);
      if (stageIndex !== -1) {
        if (data.increment) state.project.stages[stageIndex].tasksCount += 1;
        else state.project.stages[stageIndex].tasksCount -= 1;
      }
    },
    startFilterLoading(state) {
      state.filterLoading = true;
    },
    stopFilterLoading(state) {
      state.filterLoading = false;
    },
    updateStagesWithTasks(state, data) {
      state.project.stages = data;
    },
    resetTaskEvents(state) {
      if (state.taskUnderEdit && state.taskUnderEdit.taskEvents
        && state.taskUnderEdit.taskEvents.length) {
        state.taskUnderEdit.taskEvents = [];
      }
    },
    setSubtasks(state, subtasks) {
      console.log('📐📐📐 setting subtasks', {
        subtasks,
        jdumps: JSON.stringify(subtasks)
      });
      state.subtasks = subtasks;
    },
    setSubtasksLoading(state) {
      state.subtasks = null;
    },
  },
  getters: {
    sortedProjectmembers(state) {
      // Bots are project members only so their comments pass permission checks
      const getUser = store.getters['user/getUser'];
      const activeMembers = state.project && state.project.projectMembers
        ? state.project.projectMembers.filter(pm => !getUser(pm.userId)?.botClassId) : [];

      const isTablet = store.state.system.tabletSmallMode || store.state.system.tabletMode;
      let numOfUsers = MAX_USER_AVATARS_COUNT;
      if (window.devicePlatformIsMobile || isTablet) {
        numOfUsers = MAX_USER_AVATARS_COUNT_MB;
      }
      activeMembers.sort((a, b) => new Date(b.lastEventActivity) - new Date(a.lastEventActivity));

      pinCurrentUser(activeMembers);

      const hideMembers = activeMembers?.splice(numOfUsers) ?? [];
      activeMembers.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

      pinCurrentUser(activeMembers);

      hideMembers.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      return { activeMembers, hideMembers };
    },
    isUserSelectedForTasksOn(state) {
      return userId => state.projectTasksFilter.assigneeIds
        && state.projectTasksFilter.assigneeIds.includes(userId);
    },
    userIsProjectAdmin(state) {
      if (!state.project) {
        return false;
      }
      const activePm = state.project.projectMembers.find(
        pm => pm.userId === store.state.user.user.id,
      );
      return activePm && activePm.role === 'ADMIN';
    },
    getStageName(state) {
      return (stageId) => {
        if (!state.project || !state.project.stages || !state.project.stages.length) {
          return '';
        }
        if (stageId) {
          const stage = state.project.stages.find(s => s.id === stageId);

          if (stage) return stage.name;
        }

        return '';
      };
    },
    getSortedStages(state) {
      return () => {
        if (!state.project || !state.project.stages || !state.project.stages.length) {
          return [];
        }

        return state.project.stages.sort((a, b) => a.order - b.order);
      };
    },
  },
  actions: {
    async actionChangeProjectMemberTagFocus({ dispatch, state }, { projectMemberId, tagFocus, projectId }) {
      // optimistic local update
      publishLocal({
        event: 'projectMemberUpdate',
        data: {
          projectId,
          projectMember: { id: projectMemberId, tagFocus: tagFocus || null },
        },
      });
      await callAPI({
        method: 'changeProjectMemberTagFocus',
        params: { projectMemberId, tagFocus: tagFocus || null, projectId },
        persisted: true,
      });
      // Reload tasks to reflect possible changes while keeping current client-side filters intact.
      // Backend now applies projectMember.tagFocus implicitly (AND with existing tagIds) if present.
      const currentFilters = state.projectTasksFilter || {};
      await dispatch('actionFilterTasks', {
        projectId,
        ...currentFilters,
      });
    },
    async actionGetSubtasksForTask({ commit }, { parentTaskId }) {
      commit('setSubtasksLoading',);
      const subtasks = await callAPI({
        method: 'getSubtasksForTask',
        params: { parentTaskId },
        async: true,
      });
      commit('setSubtasks', subtasks);
      return subtasks;
    },
    async actionMarkAsRead(_, data) {
      await callAPI({
        method: 'readComment',
        params: data,
        persisted: true,
      });
    },
    async actionCleanUpd(_, data) {
      await callAPI({
        method: 'cleanUpd',
        params: data,
        persisted: true,
      });
    },
    async actionsArchiveProject(_, data) {
      await callAPI({
        method: 'setArchiveProject',
        params: data,
        persisted: true,
      });
    },
    async actionFilterTasks({ commit, state }, {
      searchString, assigneeIds, executorIds,
      tagIds, createdByIds, projectId, createdAtRange,
    }) {
      if (!projectId) {
        throw new Error('No projectId passed to actionFilterTasks. This staff broke our backend before..., so I throw this and do nothing');
      }
      commit('startFilterLoading');
      const filters = {
        assigneeIds: (assigneeIds && assigneeIds.length) ? assigneeIds : undefined,
        executorIds: (executorIds && executorIds.length) ? executorIds : undefined,
        tagIds: (tagIds && tagIds.length) ? tagIds : undefined,
        createdByIds: (createdByIds && createdByIds.length) ? createdByIds : undefined,
        searchString,
        createdAtRange,
      };
      lastFilterRequestId += 1;
      const requestId = lastFilterRequestId;
      const tasks = await callAPI({
        method: 'getTasksFiltered',
        params: {
          projectId,
          ...filters,
        },
      });
      // drop an answer a newer request has replaced: it would bring back older filters (the newer one stops the loading)
      if (requestId !== lastFilterRequestId) return;
      // drop the response if the user switched project while it was in flight
      if (projectId === state.project.id) {
        commit('setProjectTasksFilter', filters);
        commit('updateStagesWithTasks', tasks);
      }
      commit('stopFilterLoading');
    },
    actionSwitchFilterByFieldAndId({ state, dispatch }, { field, id }) {
      // changed in place, outside a mutation: the filters panel shows the change before the answer arrives
      const currentFilters = state.projectTasksFilter;

      if (field === 'searchString') {
        currentFilters.searchString = '';
      } else if (field === 'createdAtRange') {
        currentFilters.createdAtRange = id;
      } else {
        if (currentFilters[field] === undefined) {
          currentFilters[field] = [];
        }
        const idx = currentFilters[field].indexOf(id);
        if (idx !== -1) {
          currentFilters[field].splice(idx, 1);
        } else {
          currentFilters[field].push(id);
        }
      }
      dispatch(
        'actionFilterTasks',
        {
          projectId: state.project.id,
          assigneeIds: (currentFilters.assigneeIds && currentFilters.assigneeIds.length)
            ? currentFilters.assigneeIds : null,
          executorIds: (currentFilters.executorIds && currentFilters.executorIds.length)
            ? currentFilters.executorIds : null,
          createdByIds: (currentFilters.createdByIds && currentFilters.createdByIds.length)
            ? currentFilters.createdByIds : null,
          tagIds: (currentFilters.tagIds && currentFilters.tagIds.length)
            ? currentFilters.tagIds : null,
          searchString: currentFilters.searchString,
          createdAtRange: currentFilters.createdAtRange,
        },
      );
    },

    async actionRemoveLabel(_, { labelId, projectId }) {
      publishLocal({
        event: 'removeLabel',
        data: labelId,
      });
      await callAPI({
        method: 'removeLabel',
        params: { labelId, projectId },
      });
    },
    async actionUpdateLabel(_, {
      name,
      color,
      labelId,
      projectId,
      archived,
    }) {
      publishLocal({
        event: 'updateLabel',
        data: {
          name,
          color,
          id: labelId,
          archived,
        },
      });
      await callAPI({
        method: 'updateLabel',
        params: {
          name,
          color,
          labelId,
          projectId,
          archived,
        },
        persisted: true,
      });
    },
    async actionReorderProjectPriorities(_, { userId, projectIds }) {
      // Same numbering as the backend: the first project gets the highest priority.
      projectIds.forEach((projectId, index) => publishLocal({
        event: 'updatePriority',
        data: { userId, projectId, priority: projectIds.length - index },
      }));
      await callAPI({
        method: 'reorderProjectPriorities',
        params: { userId, projectIds },
        persisted: true,
      });
    },
    async actionRemoveNotification(_, data) {
      publishLocal({
        event: 'removeNotification',
        data: data.notificationId,
      });
      await callAPI({
        method: 'removeNotification',
        params: data,
        persisted: true,
      });
    },
    async actionAddManualTime({ commit }, data) {
      const res = await callAPI({
        method: 'addManualTime',
        params: data,
        persisted: true,
      });
      if (res?.error) {
        console.error('addManualTime refused:', res.error);
        alertManualTimeRefused(commit, 'task.manual_time_request_refused');
      }
    },
    // resolves to whether the server applied the change
    async actionUpdateManualTime({ commit, rootState }, data) {
      publishLocal({
        event: 'updateManualTime',
        data,
      });
      // a copy to put back if the server refuses the change
      const notification = data.notificationId
        ? { ...rootState.user.notifications.find(n => n.id === data.notificationId) }
        : null;
      if (data.notificationId) {
        // action called by reviewer in notifics panel
        publishLocal({
          event: 'updateNotification',
          data: {
            notificationId: data.notificationId,
            state: data.notificState,
          },
        });
        publishLocal({
          event: 'removeNotification',
          data: data.notificationId,
        });
      }
      const res = await callAPI(
        {
          method: 'updateManualTime',
          params: {
            status: data.status,
            id: data.id,
            taskHid: data.taskHid,
          },
          persisted: true,
        },
      );
      if (!res?.error) {
        return true;
      }
      console.error('updateManualTime refused:', res.error);
      if (notification) {
        commit('user/restoreNotification', notification, { root: true });
      }
      alertManualTimeRefused(commit, 'task.manual_time_update_refused');
      return false;
    },
    async actionUpdateTTBU(_, data) {
      await callAPI({
        method: 'updateTTBU',
        params: data.ttbu,
        persisted: true,
      });
    },
    async actionGetRules({ commit }, { projectId }) {
      const rules = await callAPI({
        method: 'getRules',
        params: { id: projectId },
      });
      commit('updateRules', { rules, projectId });
    },
    async actionCreateComment({ rootState }, {
      id,
      text,
      task,
      readonly,
    }) {
      console.log(text, readonly);
      const created = dayjs().utc().toISOString();
      const comment = {
        id: id || window.utils.genId(),
        textMD: '',
        textHTML: text.html,
        textDelta: JSON.stringify(text.delta),
        createdAt: created,
        updatedAt: created,
        taskId: task.id,
        userId: rootState.user.user.id,
        workspaceId: rootState.user.workspace.id,
        deleted: false,
        readonly: !!readonly,
      };

      const taskEvent = {
        id: window.utils.genId(),
        userId: rootState.user.user.id,
        action: 'add_comment',
        stageId: task.stageId,
        taskId: task.id,
        taskCommentId: comment.id,
        date: created,
      };
      publishLocal({
        event: 'createComment',
        data: { ...comment, user: { ...rootState.user.user, registrationCompleted: true }, taskEvent },
      });
     

      await callAPI({
        method: 'createComment',
        params: {
          comment,
          auth: {
            taskId: task.id,
            projectId: task.projectId,
          },
          taskEvent,
          translationStrings: {
            openTaskButton: 'Open task',
            closeButton: 'Close',
          }
        },
        persisted: true,
      });
    },
    async actionUpdateComment({ state }, {
      id,
      text,
      taskId,
      projectId,
      userId,
      taskDescription,
      eventAction,
    }) {
      const comment = {
        id,
        textMD: text.text,
        textHTML: text.html,
        textDelta: JSON.stringify(text.delta),
        taskId,
        userId,
        taskDescription,
      };

      if (eventAction === 'task_create_comment') {
        const stage = state.project?.stages?.find(s => s.tasks.findIndex(task => task.id === comment.taskId) !== -1);
        if (stage) {
          publishLocal({
            event: 'updateTask',
            data: {
              id: comment.taskId,
              textMD: comment.textMD,
              textHTML: comment.textHTML,
              textDelta: comment.textDelta,
              projectId,
              stageId: stage.id,
            },
          });
        }
      }

      publishLocal({
        event: 'updateComment',
        data: comment,
      });

      await callAPI({
        method: 'updateComment',
        params: {
          comment,
          auth: {
            taskId,
            projectId,
          },
        },
        persisted: true,
      });
    },
    async actionGetProjectLabelsData(_, { projectId }) {
      const labelsData = await callAPI({
        method: 'getProjectLabelsData',
        params: {
          projectId,
        },
      });
      return labelsData;
    },
    async actionCreateLabel(_, { projectId, name, color }) {
      return callAPI({
        method: 'createLabel',
        params: { projectId, name, color },
      });
    },
    async actionGetProjectTaskCounts(_, { projectId }) {
      return callAPI({
        method: 'getProjectTaskCounts',
        params: { projectId },
      });
    },
    async actionDeleteComment(_, { id, taskId, projectId }) {
      publishLocal({
        event: 'deleteComment',
        data: { id, taskId },
      });
      await callAPI({
        method: 'removeComment',
        params: { comment: { id, taskId }, auth: { taskId, projectId } },
        persisted: true,
      });
    },
    // Toggle the current user's reaction for one emoji on a comment (optimistic + server sync)
    async actionReactToComment({ state, commit, rootState }, { commentId, taskId, emoji }) {
      const userId = rootState.user.user.id;
      const task = state.taskUnderEdit;
      let current = {};
      if (task && task.id === taskId && task.taskEvents) {
        const event = task.taskEvents.find(e => e.taskCommentId === commentId);
        if (event && event.taskComment && event.taskComment.whoReacted) {
          const wr = event.taskComment.whoReacted;
          current = typeof wr === 'string' ? JSON.parse(wr) : wr;
        }
      }

      const whoReacted = { ...current };
      const emojiUsers = whoReacted[emoji] || [];
      if (emojiUsers.includes(userId)) {
        const next = emojiUsers.filter(id => id !== userId);
        if (next.length) whoReacted[emoji] = next;
        else delete whoReacted[emoji];
      } else {
        whoReacted[emoji] = [...emojiUsers, userId];
      }
      const userReactions = Object.keys(whoReacted).reduce((acc, e) => {
        acc[e] = whoReacted[e].length;
        return acc;
      }, {});

      commit('setCommentReactions', { commentId, taskId, userReactions, whoReacted });

      await callAPI({
        method: 'reactToComment',
        params: { commentId, taskId, emoji },
        async: true,
      });
    },
    // Lazily load the heavy who-reacted map for all currently loaded comments of a task
    async actionLoadWhoReacted({ state, commit }, { taskId }) {
      const task = state.taskUnderEdit;
      if (!task || task.id !== taskId || !task.taskEvents) return;
      const commentIds = task.taskEvents
        .filter(e => e.taskComment && e.taskComment.id)
        .map(e => e.taskComment.id);
      if (!commentIds.length) return;
      const whoReactedByComment = await callAPI({
        method: 'loadWhoReacted',
        params: { taskId, commentIds },
        async: true,
      });
      commit('setWhoReacted', { taskId, whoReactedByComment });
    },
    async deleteProject({ state }) {
      await callAPI({
        method: 'deleteProject',
        params: { projectId: state.project.id },
        persisted: true,
      });
    },
    async deleteProjectMember(_, projectMemberId) {
      publishLocal({
        event: 'excludeProjectMember',
        data: {
          projectMemberId,
        },
      });
      await callAPI({
        method: 'excludeProjectMember',
        params: { projectMemberId },
        persisted: true,
      });
    },
    async updateProjectMemberTeamRole(_, { projectMemberId, newTeamRole, projectId }) {
      publishLocal({
        event: 'changeProjectMemberTeamRole',
        data: {
          projectMemberId,
          newTeamRole,
        },
      });
      await callAPI({
        method: 'changeProjectMemberTeamRole',
        params: { projectMemberId, newTeamRole, projectId },
        persisted: true,
      });
    },
    async actionCreateProject({ commit, dispatch, rootState }, data) {
      let project = {
        id: window.utils.genId(),
        name: data.name,
        bgColor: data.bgColor,
        backgroundUrl: data.backgroundUrl,
        labels: [],
        backgroundShort: data.backgroundShort,
        backgroundAuthor: data.backgroundAuthor,
        backgroundAuthorUsername: data.backgroundAuthorUsername,
        thumbHash: data.thumbHash,
        dominantBgBrightness: calculateDominantBrightness(data.thumbHash),
        deleted: false,
        workspaceId: rootState.user.workspace.id,
        nameSlug: beautySlug({ text: data.name }),
        slug: generateHash(),
      };
      const template = JSON.parse(data.template);
      const stages = [];
      let order = 0;
      let rules = JSON.stringify(template.rules);
      template.stages.forEach((stage) => {
        order += 1;
        const newStage = {
          id: window.utils.genId(),
          name: stage,
          order,
          workspaceId: rootState.user.workspace.id,
          userId: rootState.user.user.id,
          projectId: project.id,
          tasks: [],
          deleted: false,
        };
        stages.push(newStage);

        rules = rules.replace(new RegExp(`"${stage}UUID"`, 'g'), `"${newStage.id}"`);
      });
      rules = rules.replace(new RegExp('_ME_', 'g'), `${rootState.user.user.id}`);

      const pm = {
        id: window.utils.genId(),
        approved: true,
        workspaceId: rootState.user.workspace.id,
        userId: rootState.user.user.id,
        projectId: project.id,
        role: 'ADMIN',
        lastActivity: dayjs().utc(),
      };
      const projectMembers = [
        {
          ...pm,
          user: { ...rootState.user.user, registrationCompleted: true },
        },
      ];
      project = {
        ...project,
        stages,
        rules,
        projectMembers,
      };
      publishLocal({
        event: 'updateProjectInList',
        data: {
          ...project,
          projectMembers: [{ lastActivity: pm.lastActivity }],
        },
      });
      // commit('setPreloadProject', project);
      commit('setCurrentProject', project);
      await callAPI({
        method: 'createProject',
        params: { project },
        persisted: true,
      });
      stages.forEach((stage) => {
        const { user, ...readyToCommit } = stage;
        callAPI({
          method: 'createStage',
          params: { readyToCommit, projectId: project.id },
          persisted: true,
        });
      });
      dispatch('actionGetProjectsList');
    },
    async actionGetProject({ commit, state, rootState, dispatch }, data) {
      console.log('🚩 actionGetProject', data);
      // Reset integrations while project is loading
      commit('setProjectIntegrations', null);
      // todo remove for now, because recursive issues
      // if (data.slug !== 'last') {
      //   commit('setPreloadProject', state.projectsList.find(p => p.slug === data.slug));
      // }
      commit('setCurrentProject', skeletonProjectData);

      // setCurrentProject will naturally load tasks from scratch so we have to reset filters
      commit('setProjectTasksFilter', {});
      const proj = await callAPI({
        method: 'getProjectAndSubscribe',
        async: true,
        params: data,
      });
      if (proj.denyAccess) {
        openLastProjectOfCurrentWorkspace();
      }
      if (proj.id === null) {
        proj.name = 'No projects';
      }

      commit('setPreloadProject', proj);
      commit('setCurrentProject', proj);
      if (proj.id) {
        dispatch('user/actionClearProjectInvitationNotifications', { projectId: proj.id }, { root: true });
      }
      // Tag focus no longer forces a client-side tagIds override here; server applies it implicitly.
      // This keeps UI filters truthful. If tagIds are set explicitly by user they will AND with tagFocus server-side.
      // Load integrations after project is set
      try {
        await this.dispatch('project/actionGetAllProjectIntegrations');
      } catch (e) {
        console.error('Failed to load project integrations', e);
      }
      return proj;
    },
    async actionGetAllProjectIntegrations({ commit, state }) {
      if (!state.project || !state.project.id) {
        commit('setProjectIntegrations', {});
        return {};
      }
      const mapping = await callAPI({
        method: 'getAllProjectIntegrations',
        params: { projectId: state.project.id },
      });
      commit('setProjectIntegrations', mapping || {});
      return mapping || {};
    },
    async actionGetProjectsList({ commit }, data) {
      const projList = await callAPI({
        method: 'getProjectsListAndSubscribe',
        params: data,
      });
      commit('setProjectsList', projList);
    },
    async actionGetProjectsListArchived({ commit, state }, data = {}) {
      if (state.projectsArchivedListLoading) {
        return state.projectsArchivedList;
      }
      if (state.projectsArchivedListLoaded && !data.force) {
        return state.projectsArchivedList;
      }

      commit('setProjectsArchivedListLoading', true);
      try {
        const projList = await callAPI({
          method: 'getProjectsListArchived',
          params: data,
        });
        commit('setProjectsArchivedList', projList || []);
        return projList || [];
      } finally {
        commit('setProjectsArchivedListLoading', false);
      }
    },
    async actionGetProjectsListWithMembers({ commit }, data) {
      const projListWithMembers = await callAPI({
        method: 'getProjectsListWithMembersAndSubscribe',
        params: data,
      });
      commit('setProjectsListWithMembers', projListWithMembers);
    },
    async actionGetTemplates({ commit }, data) {
      const templates = await callAPI({
        method: 'getTemplates',
        params: data,
      });
      commit('setTemplates', templates);
    },
    async actionDeleteTask({ commit, dispatch }, task) {
      publishLocal({
        event: 'deleteTask',
        data: task,
      });
      await callAPI({
        method: 'deleteTask',
        params: { id: task.id, projectId: task.projectId },
        persisted: true,
      });

      commit('mutationPushActionAlert', {
        actionText: `${i18n.global.t('notifications.Task')}
          <i>${task.textMD.substring(0, 70).trim()}${task.textMD.length > 70 ? '...' : ''}</i>
          ${i18n.global.t('notifications.task_deleted')}`,
        grayButton: {
          text: `${i18n.global.t('notifications.Restore')}`,
          method: () => {
            dispatch('actionRestoreTask', task);
          },
        },
        confirmButton: {
          text: 'OK',
          variant: 'red',
          method: () => { commit('mutationShiftActionAlert'); },
        },
      });
    },
    async actionTransferTask({ commit, dispatch }, { task, targetProject }) {
      console.log('Transferring task', task, 'to project', targetProject);
      const tid = window.utils.genId();
      const firstStage = targetProject.stages.find(stage => stage.order === 1 && !stage.deleted);
      const newCommentId = window.utils.genId();
      const taskPreview = task.attachments.find((a) => {
        if (a.type === 'TASK_PREVIEW') {
          const createTaskEvent = task.taskEvents.find(e => e.action === 'task_create_comment');
          if (a.commentId === createTaskEvent.taskComment.id) {
            return true;
          }
        }
        return false;
      });
      const projectWithMembers = await callAPI({ 
        method: 'getProjectInfo', 
        async: true,
        params: { projectId: targetProject.id, labels: false, members: true } 
      });
      const subTasks = task.isParentTask ? await callAPI({
        method: 'getSubtasksForTask',
        async: true,
        params: { parentTaskId: task.id, includeTaskEvents: true },
      }) : null;
      const projectMemberIds = projectWithMembers.members.map(item => item.id);

      const ttbus = [];

      if (task.assigneeId && projectMemberIds.includes(task.assigneeId)) {
        ttbus.push({
          id: window.utils.genId(),
          userId: task.assigneeId,
          projectId: targetProject.id,
          taskId: tid,
          totalSeconds: 0,
          unreadComments: 0,
          trackingOnline: false,
          timeSlots: [],
        });
      }
      if (task.executorId && projectMemberIds.includes(task.executorId) && task.executorId !== task.assigneeId) {
        ttbus.push({
          id: window.utils.genId(),
          userId: task.executorId,
          projectId: targetProject.id,
          taskId: tid,
          totalSeconds: 0,
          unreadComments: 0,
          trackingOnline: false,
          timeSlots: [],
        });
      }

      if (!firstStage) {
        store.commit('system/showAlert', {
          timeout: 10,
          message: "Target project doesn't have any stages to transfer task into. Please create at least one stage.",
        });
        return;
      }
      const newTask = {
        id: tid,
        order: 1,
        textMD: task.textMD,
        textHTML: task.textHTML,
        stageId: firstStage.id,
        projectId: targetProject.id,
        creatorId: projectMemberIds.includes(task.creatorId) ? task.creatorId : store.state.user.user.id,
        transferredFromTaskId: task.id,
        slug: generateHash(),
        assigneeId: projectMemberIds.includes(task.assigneeId) ? task.assigneeId : undefined,
        executorId: projectMemberIds.includes(task.executorId) ? task.executorId : undefined,
        commentId: newCommentId,
        labels: [],
        priority: task.priority,
        hid: '1',
        taskTrackedByUsers: ttbus,
        parentTaskId: task.parentTaskId,
      };

      const createdTask = await callAPI({
        method: 'createTask',
        params: newTask,
        persisted: true,
      });
      if (taskPreview) {
        copyAttachmentToTask(taskPreview, {
          id: window.utils.genId(),
          width: taskPreview.width,
          height: taskPreview.height,
          extension: taskPreview.extension,
          type: 'TASK_PREVIEW',
          workspaceId: task.workspaceId,
          projectId: newTask.projectId,
          taskId: newTask.id,
          deleted: false,
          commentId: newCommentId,
          dominantColor: taskPreview.dominantColor,
          thumbHash: taskPreview.thumbHash,
        });
      }



      function sleep(ms) {
        const start = Date.now();
        while (Date.now() - start < ms);
      }

      if (task.id) {
        const sortedEvents = task.taskEvents.sort((a, b) => dayjs(a.date) - dayjs(b.date));
        sortedEvents.forEach(async (tEvent) => {
          if (tEvent.action === 'add_comment' && tEvent.taskComment.deleted === false) {
            // The forEach is too fast.
            // This "sleep" is necessary because sometimes dayjs generates the same time for two events
            sleep(50);
            const created = dayjs().utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
            const safeTextMD = projectMemberIds.includes(tEvent.userId) ? tEvent.taskComment.textMD : `(forwarded from ${tEvent.user.username}) ${tEvent.taskComment.textMD}`;
            const safeTextHTML = projectMemberIds.includes(tEvent.userId) ? tEvent.taskComment.textHTML : `<p><strong>(forwarded from ${tEvent.user.username})</strong></p>${tEvent.taskComment.textHTML}`;
            const safeTextDelta = JSON.parse(tEvent.taskComment.textDelta);
            if (projectMemberIds.includes(tEvent.userId)) {
              safeTextDelta.ops.unshift({ attributes: { bold: true }, insert: `(forwarded from ${tEvent.user.username})` });
            }
            const commentUser = projectMemberIds.includes(tEvent.userId) ? tEvent.user : store.state.user.user;
            const comment = {
              id: window.utils.genId(),
              textMD: safeTextMD,
              textHTML: safeTextHTML,
              textDelta: JSON.stringify(safeTextDelta),
              createdAt: created,
              updatedAt: created,
              taskId: createdTask.id,
              userId: commentUser.id,
              workspaceId: tEvent.taskComment.workspaceId,
              deleted: false,
              readonly: tEvent.taskComment.readonly,
            };
            const taskEvent = {
              id: window.utils.genId(),
              userId: commentUser.id,
              action: 'add_comment',
              stageId: firstStage.id,
              taskId: newTask.id,
              taskCommentId: comment.id,
              date: created,
            };

            publishLocal({
              event: 'createComment',
              data: { ...comment, user: { ...commentUser, registrationCompleted: true }, taskEvent },
            });
            await callAPI({
              method: 'createComment',
              params: {
                comment,
                auth: {
                  taskId: newTask.id,
                  projectId: newTask.projectId,
                },
                taskEvent,
              },
              persisted: true,
            });
            task.attachments.forEach((tAttachment) => {
              if (tAttachment.commentId === tEvent.taskComment.id) {
                const isImage = tAttachment.width && tAttachment.height;
                copyAttachmentToTask(tAttachment, {
                  id: window.utils.genId(),
                  width: tAttachment.width,
                  height: tAttachment.height,
                  extension: tAttachment.extension,
                  filename: tAttachment.filename,
                  size: tAttachment.size,
                  type: isImage && !taskPreview ? 'TASK_PREVIEW' : 'TASK_ATTACHMENT',
                  workspaceId: comment.workspaceId,
                  projectId: newTask.projectId,
                  taskId: newTask.id,
                  deleted: false,
                  commentId: comment.id,
                  dominantColor: tAttachment.dominantColor,
                  thumbHash: tAttachment.thumbHash,
                  order: tAttachment.order,
                });
              }
            });
          }
        });
      }
      if (createdTask.id) {
        const taskTransferEvent = {
          id: utils.genId(),
          userId: store.state.user.user.id,
          action: 'transfer_task',
          stageId: createdTask.stageId,
          taskId: createdTask.id,
          payload: JSON.stringify({
            fromProjectId: task.projectId,
            fromProjectName: task.project ? task.project.name : '',
          }),
          date: dayjs().utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
        };
        await callAPI({
          method: 'addEvent',
          params: taskTransferEvent,
          persisted: true,
        });
        await callAPI({
          method: 'deleteTask',
          params: { id: task.id, projectId: task.projectId },
          persisted: true,
        });
      }
      for (const subtask of subTasks || []) {
        subtask.parentTaskId = createdTask.id;
        await dispatch('actionTransferTask', { task: subtask, targetProject });
      }
      if (task.isParentTask) {
        commit('mutationPushActionAlert', {
          actionText: `${i18n.global.t('notifications.Task')}
            <i>${task.textMD.substring(0, 70).trim()}${task.textMD.length > 70 ? '...' : ''}</i>
            ${i18n.global.t('notifications.task_transferred')}`,
          grayButton: {
            text: `${i18n.global.t('search.open')}`,
            method: () => {
              dispatch('actionGetTaskAndOpen', { id: tid });
              commit('mutationShiftActionAlert');
            },
          },
          confirmButton: {
            text: 'OK',
            variant: 'red',
            method: () => { commit('mutationShiftActionAlert'); },
          },
        });
      }
      return createdTask;
    },
    async actionRestoreTask({ commit, dispatch }, task) {
      await callAPI({
        method: 'restoreTask',
        params: { id: task.id, projectId: task.projectId },
        persisted: true,
      });

      commit('mutationShiftActionAlert');

      commit('mutationPushActionAlert', {
        actionText: `${i18n.global.t('notifications.Task')}
          <i>${task.textMD.substring(0, 70).trim()}${task.textMD.length > 70 ? '...' : ''}</i>
          ${i18n.global.t('notifications.task_restored')}`,
        grayButton: {
          text: `${i18n.global.t('search.open')}`,
          method: () => {
            dispatch('actionGetTaskAndOpen', { id: task.id });
            commit('mutationShiftActionAlert');
          },
        },
        confirmButton: {
          text: 'OK',
          variant: 'red',
          method: () => { commit('mutationShiftActionAlert'); },
        },
      });
    },
    async actionAttachImageToTask(_, {
      data,
      width,
      height,
      updTaskId,
      updProjectId,
      updStageId,
      updWorkspaceId,
      hasAttachments,
      attachmentId,
      commentId,
      nopreview,
      order,
    }) {
      const mime = data.split(';')[0].split(':')[1];
      const extension = mime.split('/')[1];
      const file = await (await fetch(data)).blob();
      const attachment = {
        // an edited image keeps its id, so it replaces the stored one
        id: attachmentId || window.utils.genId(),
        width,
        height,
        extension,
        type: hasAttachments || nopreview ? 'TASK_ATTACHMENT' : 'TASK_PREVIEW',
        workspaceId: updWorkspaceId,
        projectId: updProjectId,
        taskId: updTaskId,
        deleted: false,
        commentId,
        dominantColor: await window.utils.getColorOfB64(data),
        thumbHash: await imageToThumbHash(file),
        order,
      };
      publishLocal({
        event: 'addAttachment',
        data: {
          attachment: { ...attachment, uploaded: false, basePicture: data },
          taskId: updTaskId,
          stageId: updStageId,
        },
      });
      enqueueUpload({ file, name: `image.${extension}`, taskId: updTaskId, attachment });
    },
    getWorkspaceLabels() {
      return callAPI({
        method: 'getWorkspaceLabels',
        params: {},
      });
    },
    async actionUploadAttachment(_, {
      blobData,
      taskId,
      stageId,
      projectId,
      commentId,
      workspaceId,
      attachmentId,
      filename,
    }) {
      const nameArray = filename.split('.');
      const attachment = {
        id: attachmentId || window.utils.genId(),
        extension: nameArray.pop(),
        filename: nameArray.join('.'),
        size: blobData.size,
        type: 'TASK_ATTACHMENT',
        workspaceId,
        projectId,
        taskId,
        deleted: false,
        commentId,
      };
      publishLocal({
        event: 'addAttachment',
        data: {
          attachment: { ...attachment, uploaded: false },
          taskId,
          stageId,
        },
      });
      enqueueUpload({
        file: blobData,
        name: filename,
        taskId,
        attachment,
        // Mac/Safari does not support webm and mkv so we need to convert them to mp4
        convertationRequired: attachment.extension === 'webm' || attachment.extension === 'mkv',
      });
    },
    async actionUpdateProject({ state }, data) {
      publishLocal({
        event: 'updateProject',
        data,
      });
      publishLocal({
        event: 'updateProjectInList',
        data: {
          ...data,
          id: state.project.id,
        },
      });
      callAPI({
        method: 'configureProject',
        params: {
          ...data,
          projectId: state.project.id,
        },
        persisted: true,
      });
    },
    async actionUpdateStage({ state }, stage) {
      publishLocal({
        event: 'updateStage',
        data: {
          ...stage,
          projectId: state.project.id,
        },
      });
      await callAPI({
        method: 'updateStage',
        params: {
          ...stage,
          projectId: state.project.id,
        },
        persisted: true,
      });
    },
    async actionInviteUserToProject({ rootState }, inviteData) {
      let { user } = inviteData;
      if (!user) {
        user = {
          workspaceId: rootState.user.workspace.id,
          username: inviteData.email,
          email: inviteData.email,
          role: 'GUEST',
          id: window.utils.genId(),
          registrationCompleted: false,
        };
        publishLocal({
          event: 'workspaceMemberUpdate',
          local: true,
          data: user,
        });
      }
      const memberConnectData = {
        projectId: inviteData.projectId,
        projectMember: {
          id: window.utils.genId(),
          approved: !inviteData.user,
          userId: user.id,
        },
        createUser: !inviteData.user,
      };

      publishLocal({
        event: 'projectMemberUpdate',
        data: memberConnectData,
      });
      return callAPI({
        method: 'inviteUserToProject',
        params: {
          ...memberConnectData,
          locale: rootState.user.workspace.locale,
          user,
        },
        persisted: true,
      });
    },
    async actionReinviteUser(_, data) {
      return callAPI({
        method: 'reinviteUser',
        params: data,
        persisted: true,
      });
    },
    async actionMoveTask(_, task) {
      publishLocal({
        event: 'dragTask',
        data: task,
      });
      await callAPI({
        method: 'moveTask',
        params: task,
        persisted: true,
      });
    },
    async actionUpdateTask({ state }, task) {
      const updTask = { ...task };
      if (!updTask.projectId) {
        throw new Error('projectId is required for task update');
      }
      if (updTask.textMD) {
        updTask.textHTML = md.render(updTask.textMD);
      }
      publishLocal({
        event: 'updateTask',
        data: updTask,
      });

      await callAPI({
        method: 'updateTask',
        params: updTask,
        persisted: true,
      });

    },
    async reconnectionToServer({ state }) {
      if (state.project && state.taskUnderEdit) {
        await callAPI({
          method: 'getTaskAndSubscribe',
          params: {
            id: state.taskUnderEdit.id,
          },
        });
        await callAPI({
          method: 'getProjectAndSubscribe',
          params: {
            slug: state.project.slug,
            tasksLimit: 10,
          },
        });
      }
    },
    async actionGetTask({ commit }, task) {
      const res = await callAPI({
        method: 'getTaskAndSubscribe',
        params: task,
        async: true, // to show content faster if there are other calls
      });
      commit('setOpenedTask', res);
    },
    async actionGetTaskAndOpen({ commit }, task) {
      const res = await callAPI({
        method: 'getTaskAndSubscribe',
        async: true, // to show content faster if there are other calls
        params: task,
      });
      commit('mutationOpenTaskPopup', { task: { ...res } });
    },
    async actionDeleteScreenshot(_, screenshot) {
      publishLocal({
        event: 'deleteScreenshot',
        data: screenshot,
      });
      await callAPI({
        method: 'deleteScreenshot',
        params: screenshot,
        persisted: true,
      });
    },
    async actionSetTaskPreview(_, attachment) {
      const updTask = {
        id: attachment.taskId,
        projectId: attachment.projectId,
      };

      await callAPI({
        method: 'setTaskPreview',
        params: attachment,
        persisted: true,
      });
    },
    async actionDeleteAttachment(_, attachment) {
      publishLocal({
        event: 'deleteAttachment',
        data: attachment,
      });
      await callAPI({
        method: 'deleteAttachment',
        params: attachment,
        persisted: true,
      });
    },
    async actionReorderAttachments(_, { taskId, orderedIds }) {
      orderedIds.forEach((id, order) => {
        publishLocal({
          event: 'updateAttachment',
          data: { id, taskId, order },
        });
      });
      await callAPI({
        method: 'reorderAttachments',
        params: { taskId, orderedIds },
        persisted: true,
      });
    },
    async actionCreateStage({ state, dispatch }, { name }) {
      const stage = {
        id: window.utils.genId(),
        order: state.project.stages.length
          ? Math.max(...state.project.stages.map(s => s.order)) + 1 : 1,
        name,
        projectId: state.project.id,
        deleted: false,
      };
      publishLocal({
        event: 'updateStage',
        data: stage,
      });
      const rules = [...state.project.rules, {
        name,
        stageId: stage.id,
        buttons: { secondary: [{ action: 'MOVE', toStageId: 'prev_stage' }], primary: [{ action: 'MOVE', toStageId: 'next_stage' }] },
      }];
      dispatch('actionUpdateProject', { rules: JSON.stringify(rules) });
      await callAPI({
        method: 'createStage',
        params: stage,
        persisted: true,
      });
    },
    async actionDeleteStage(_, stage) {
      publishLocal({
        event: 'deleteStage',
        data: stage.id,
      });
      await callAPI({
        method: 'deleteStage',
        params: { id: stage.id, projectId: stage.projectId },
        persisted: true,
      });
    },
    async actionGetElementsPaged({ commit }, query) {
      const res = await callAPI({
        method: 'getElementsPaged',
        params: query,
        async: true, // to show content faster if there are other calls
      });
      
      commit('setElements', { ...res, category: query.category });
      return res;
    },
    async actionGetBgPaged(_, query) {
      const res = await callAPI({
        method: 'getUnsplashImages',
        params: query,
      });
      return res;
    },
    async actionUnsubTask(_, id) {
      const res = await callAPI({
        method: 'unsubTask',
        params: { taskId: id },
      });
      return res;
    },
    async actionGetTasksForStage({ state, commit }, {
      projectId,
      stageId,
      limit,
      offset,
      viewType,
    }) {
      const tasksData = await callAPI({
        method: 'getTasksForStage',
        params: {
          projectId,
          stageId,
          limit,
          offset,
          viewType,
          search: state.projectTasksFilter.searchString,
          assigneeIds: state.projectTasksFilter.assigneeIds,
          executorIds: state.projectTasksFilter.executorIds,
          tagIds: state.projectTasksFilter.tagIds,
          createdByIds: state.projectTasksFilter.createdByIds,
          createdAtRange: state.projectTasksFilter.createdAtRange,
        },
      });
      commit('addTasksToStage', {
        stageId,
        tasks: tasksData.tasks,
      });
      return tasksData;
    },
    async actionCalculateETAByTag(_, {
      tagId,
      projectId,
      sprintSettings,
    }) {
      const tasksData = await callAPI({
        method: 'calculateETAByTag',
        params: {
          tagId,
          projectId,
          sprintSettings,
        },
        persisted: true,
      });
      return tasksData;
    },
    async actionGetNextTaskOrder(_, { stageId, offset }) {
      return callAPI({
        method: 'getNextTaskOrder',
        params: { stageId, offset },
      });
    },
    actionResetTaskEvents({ commit }) {
      commit('resetTaskEvents');
    },
    async actionChangeDoingScreenshot({ commit, state }, flag) {
      await callAPI({
        method: 'changeEnableScreenshots',
        params: { status: flag, projectId: state.project.id },
        persisted: true,
      });
      commit('mutationEnableScreenshots', flag);
    },
    async actionRunRules({ dispatch, state }, { task, stageRules, type, mentionId, callbackCloseDialog }) { // eslint-disable-line
      const selectedRule = stageRules[type];
      const currentUser = store.state.user.user;
      const currentAssignee = task.assigneeId;
      console.log('actionRunRules selectedRule', selectedRule);

      const closerTask = (userId) => {
        if (currentUser.id === currentAssignee && userId !== currentAssignee && callbackCloseDialog) { // close task if user loose assignee
          callbackCloseDialog();
        }
      };

      const getUserId = (action) => {
        if (action.assignId === 'creator') {
          return task.creatorId;
        }

        if (action.assignId === '_me_') {
          return store.state.user.user.id;
        }

        return action.assignId;
      };

      const handlerRules = { // eslint-disable-line
        [EVENT__MOVE]: async (action) => {
          if (!action?.toStageId) {
            console.log('⛔️❓⛔️❔⛔️ Can\'t move task: stage to move task to is not specified in action =>', action);
            return false;
          }
          let projectStages = state.project.stages;

          if (state.project.id !== task.projectId) {
            projectStages = await callAPI({
              method: 'getProjectStages',
              params: {
                projectId: task.projectId,
                tasksLimit: 2,
              },
              persisted: true,
            });
          }

          let newStage = projectStages.find(x => x.id === action.toStageId);

          if (!newStage) { // previous or next stage
            const currentStageTaskIdx = projectStages?.findIndex(x => x.id === task.stageId);
            if (currentStageTaskIdx === -1 || currentStageTaskIdx === undefined) {
              console.log('⛔️ 📝➡️❌ 4️⃣0️⃣4️⃣ ⛔️ Can\'t move task, target stage is not found in the current project');
              return false;
            }
            if (action.toStageId === 'prev_stage') {
              newStage = projectStages[currentStageTaskIdx - 1];
            } else if (action.toStageId === 'next_stage') {
              newStage = projectStages[currentStageTaskIdx + 1];
            }
          }

          if (!newStage) {
            console.log('We can\'t move task, no candidate found', action);
            return false;
          }

          const moveState = {
            task: {
              id: task.id,
              projectId: task.projectId,
              stageId: newStage.id,
              prevStageId: null,
              prevOrder: null,
            },
            stageIdFrom: task.stageId,
          };

          dispatch('actionMoveTask', moveState);
          return true;
        },
        [EVENT__ASSIGN]: (action) => {
          if (!action?.assignId) {
            console.log('⛔️⛔️⛔️⛔️ sorry, but we can\'t assign task', action);
            return false;
          }

          const assigneeId = getUserId(action);
          if (assigneeId) {
            dispatch('actionUpdateTask', {
              id: task.id,
              assigneeId,
              projectId: task.projectId,
            });

            closerTask(assigneeId);
          }
          return true;
        },
        [EVENT__ASSIGN_IF_NOTASSIGNED]: (action) => {
          if (!action?.assignId) {
            return false;
          }
          const userId = getUserId(action);
          if (userId && !task.assigneeId) {
            dispatch('actionUpdateTask', {
              id: task.id,
              assigneeId: userId,
              projectId: task.projectId,
            });
            closerTask(userId);
          }
          return true;
        },
        [EVENT__ASSIGN_MENTION]: () => {
          if (!mentionId) {
            return false;
          }
          dispatch('actionUpdateTask', {
            id: task.id,
            assigneeId: mentionId,
            projectId: task.projectId,
          });

          closerTask(mentionId);
          return true;
        },
        [EVENT__AUTOCLICK]: () => {},
        [EVENT__UNASSIGN]: () => {
          if (task.assigneeId) {
            dispatch('actionUpdateTask', {
              id: task.id,
              assigneeId: null,
              projectId: task.projectId,
            });
            closerTask(null);
          }
          return true;
        },
      };
      /**
       * @param {Array[]} EXISTING__GROUPS
       * @param {string} EXISTING__GROUPS[]
       * @returns {Array.<Array<Object>>}
       */
      const filterRules = EXISTING__GROUPS
        .map(itemGroup => Lodash
          .findLast(selectedRule, rule => itemGroup
            .find(item => item === rule.action)));
      filterRules.forEach(element => element && handlerRules[element.action](element));
    },
    async actionGetProjectInfo(_, { projectId, labels, members }) {
      const response = await callAPI({ method: 'getProjectInfo', params: { projectId, labels, members } });
      return response;
    },

    async actionCreateTaskFromPopup({
      state, rootState, commit, dispatch,
    }, data) {
      const [stageId, order] = (() => {
        if (data.projectId !== state.project.id) {
          return [null, null];
        }
        const stage = data.stageId
          ? state.project.stages.find(s => s.id === data.stageId)
          : [...state.project.stages].sort((a, b) => (a.order - b.order))[0];
        const orderNum = stage.tasks.length ? Math.max(...stage.tasks.map(s => s.order)) + 1 : 1;
        return [stage.id, orderNum];
      })();
      const newCommentId = window.utils.genId();
      const tid = window.utils.genId();

      const images = await Promise.all(data.images.map(async (image, index) => {
        const file = await (await fetch(image.image)).blob();
        return {
          file,
          attachment: {
            id: image.id,
            width: image.width,
            height: image.height,
            extension: file.type.split('/')[1],
            type: index ? 'TASK_ATTACHMENT' : 'TASK_PREVIEW',
            workspaceId: rootState.user.workspace.id,
            projectId: data.projectId,
            taskId: tid,
            deleted: false,
            commentId: newCommentId,
            dominantColor: await window.utils.getColorOfB64(image.image),
            thumbHash: await imageToThumbHash(file),
            order: index,
          },
        };
      }));

      // insert creator instantly / so he will see task in recent tasks 
      const taskTrackedByUsers = [
        {
          id: window.utils.genId(),
          userId: rootState.user.user.id,
          projectId: data.projectId,
          taskId: tid,
          workspaceId: rootState.user.workspace.id,
          totalSeconds: 0,
          unreadComments: 0,
          trackingOnline: false,
          timeSlots: [],
          lastStarted: dayjs().utc().toISOString(),
        },
      ]
      if ( data.assigneeId && !taskTrackedByUsers.some(t => t.userId === data.assigneeId) ) {
        taskTrackedByUsers.push({
          id: window.utils.genId(),
          userId: data.assigneeId,
          projectId: data.projectId,
          taskId: tid,
          workspaceId: rootState.user.workspace.id,
          totalSeconds: 0,
          unreadComments: 0,
          trackingOnline: false,
          timeSlots: [],
        });
      }


      const task = {
        id: tid,
        order,
        textMD: data.text,
        textHTML: md.render(`${data.text}\n\n`),
        // textDelta: `{"ops":[{"insert":"${data.text}\n"}]}`,
        stageId,
        projectId: data.projectId,
        creatorId: rootState.user.user.id,
        slug: generateHash(),
        assigneeId: data.assigneeId,
        executorId: data.assigneeId ? data.assigneeId : undefined,
        commentId: newCommentId,
        labels: data.labels,
        priority: data.priority,
        hid: '1',
        taskTrackedByUsers,
        parentTaskId: data.parentTaskId,
      };

      if (data.parentTaskId) {
        // also ensure current task is parent
        publishLocal({
          event: 'updateTask',
          data: { id: data.parentTaskId, isParentTask: true, projectId: data.projectId || state.project.id },
        });
      }

      if (data.projectId === state.project.id) {
        publishLocal({
          event: 'updateTask',
          data: {
            ...task,
            attachments: images.map(({ attachment }, index) => ({
              ...attachment, uploaded: false, basePicture: data.images[index].image,
            })),
            taskEvents: [],
            screenshots: [],
          },
        });
      }
      const createdTask = await callAPI({
        method: 'createTask',
        params: task,
        persisted: true,
      });
      // the images are stored only after the task they belong to
      images.forEach(({ file, attachment }) => enqueueUpload({
        file, name: `image.${attachment.extension}`, taskId: tid, attachment,
      }));
      
      // update recent tasks to reflect recently created task
      store.dispatch('user/actionGetMyRecentTasks', null, { root: true });
      

      commit('mutationPushActionAlert', {
        actionText: `${i18n.global.t('notifications.Task')}
        <i>${task.textMD.substring(0, 70).trim()}${task.textMD.length > 70 ? '...' : ''}</i>
        ${i18n.global.t('notifications.task_created')}`,
        grayButton: {
          text: `${i18n.global.t('search.open')}`,
          method: () => {
            dispatch('actionGetTaskAndOpen', { id: task.id });
            commit('mutationShiftActionAlert');
          },
        },
        confirmButton: {
          text: 'OK',
          variant: 'accent',
          method: () => { commit('mutationShiftActionAlert'); },
        },
      });
      return createdTask;
    },
  },
};
